/**
 * Auth utility functions for handling JWT tokens and authentication
 */

/**
 * Get the authentication token from localStorage
 * @returns {string|null} The auth token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Set the authentication token in localStorage
 * @param {string} token - The JWT token to store
 */
export const setAuthToken = (token) => {
  localStorage.setItem('authToken', token);
};

/**
 * Remove the authentication token from localStorage
 */
export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
};

/**
 * Check if the user is authenticated (has a token)
 * @returns {boolean} True if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Parse the JWT token to get user data
 * @returns {Object|null} Decoded token payload or null if invalid
 */
export const getDecodedToken = () => {
  const token = getAuthToken();
  if (!token) return null;
  
  try {
    // JWT token consists of three parts separated by dots
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if the current user has a specific role
 * @param {string|string[]} roles - Role or array of roles to check
 * @returns {boolean} True if user has any of the specified roles
 */
export const hasRole = (roles) => {
  const decodedToken = getDecodedToken();
  if (!decodedToken || !decodedToken.role) return false;
  
  const userRole = decodedToken.role;
  if (Array.isArray(roles)) {
    return roles.includes(userRole);
  }
  return userRole === roles;
}; 